import React, { useState, useMemo } from 'react';
import { IioTable } from '../../components/IioTable';
import { StarButton } from '../../components/StarButton';
import { Project, ProjectStatus, isAdmin } from '../../types/project';
import { CreditCardModal } from '../../components/modals/CreditCardModal';
import { capitalize } from '../../utilities/format';
import { formatLocaleDate } from '../../utilities/date';
import { SortableTh } from '../../components/SortableTh';
import { compareBy, type SortDir } from '../../utilities/sort';

const STATUS_ROW_CLASS: Partial<Record<ProjectStatus, string>> = {
  trial: 'info',
  hibernate: 'warning',
  purged: 'danger',
};

type SortField = 'name' | 'companyFirmName' | 'createdAt';

function ProjectNameCell({ project }: { project: Project }) {
  const active = project.status === 'active' || project.status === 'trial';
  const hibPurged = project.status === 'hibernate' || project.status === 'purged';

  let link: React.ReactNode;
  if (active) {
    const href = project.numCollections > 0
      ? `#!/project/${project.id}`
      : `#!/project/${project.id}/getstarted`;
    link = <a href={href}>{project.name}</a>;
  } else if (hibPurged) {
    link = isAdmin(project)
      ? <a href={`#!/project/${project.id}/admin`}>{project.name}</a>
      : <span>{project.name}</span>;
  } else {
    link = <a href={`#!/project/${project.id}`}>{project.name}</a>;
  }

  return (
    <>
      {link}
      {project.description && <><br /><small>{project.description}</small></>}
    </>
  );
}

function StatusCell({ project }: { project: Project }) {
  const [showUpgrade, setShowUpgrade] = useState(false);
  return (
    <>
      {capitalize(project.status)}
      {project.status === 'hibernate' && project.hibernateDate && (
        <small className="text-muted"> {formatLocaleDate(project.hibernateDate)}</small>
      )}
      {project.status === 'trial' && (
        <> <button className="btn btn-xs btn-info" onClick={() => setShowUpgrade(true)}>
          Upgrade <i className="fas fa-arrow-circle-right" aria-hidden="true" />
        </button></>
      )}
      {showUpgrade && <CreditCardModal projectId={project.id} onClose={() => setShowUpgrade(false)} />}
    </>
  );
}

function ProjectRoles({ project }: { project: Project }) {
  if (!project.roles?.length) return null;
  return (
    <>
      {project.roles.map((role, i) =>
        role.permissions?.includes('ADMIN_PROJECT')
          ? <a key={i} className="btn btn-default btn-xs" href={`#!/project/${project.id}/admin`}>
              <i className="fas fa-cog" aria-hidden="true" /> Project Admin
            </a>
          : <span key={i} className="label label-default">{role.name}</span>
      )}
    </>
  );
}

export interface ProjectTableProps {
  projects: Project[];
  hideCompany?: boolean;
  hideRoles?: boolean;
}

export function ProjectTable({ projects, hideCompany = false, hideRoles = false }: ProjectTableProps) {
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>({
    field: 'createdAt',
    dir: 'desc',
  });

  const statuses = useMemo(() => [...new Set(projects.map(p => p.status))], [projects]);

  const filtered = useMemo(() => {
    let result = projects;
    if (nameFilter) {
      const q = nameFilter.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) || (p.companyFirmName ?? '').toLowerCase().includes(q)
      );
    }
    if (statusFilter) result = result.filter(p => p.status === statusFilter);
    return [...result].sort(compareBy(p => (p[sort.field] ?? '') as string, sort.dir));
  }, [projects, nameFilter, statusFilter, sort]);

  const toggleSort = (field: SortField) =>
    setSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc',
    }));

  if (projects.length === 0) return <div className="alert">No Projects Found</div>;

  return (
    <IioTable>
      <thead>
        <tr>
          <th>&nbsp;</th>
          <SortableTh label="Name" sortKey="name" current={sort.field} dir={sort.dir} onSort={toggleSort} />
          {!hideCompany && <SortableTh label="Company/Firm" sortKey="companyFirmName" current={sort.field} dir={sort.dir} onSort={toggleSort} />}
          <SortableTh label="Created" sortKey="createdAt" current={sort.field} dir={sort.dir} onSort={toggleSort} />
          <th>Collections</th>
          <th>Status</th>
          {!hideRoles && <th>Role</th>}
        </tr>
      </thead>
      <tbody>
        <tr>
          <th colSpan={hideCompany ? 3 : 4}>
            <input
              type="search"
              className="input-sm form-control"
              placeholder="Project Filter"
              value={nameFilter}
              onChange={e => setNameFilter(e.target.value)}
            />
          </th>
          <th colSpan={2}>
            <select
              className="form-control input-sm"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              {statuses.map(s => <option key={s} value={s}>{capitalize(s)}</option>)}
            </select>
          </th>
        </tr>
        {filtered.map(project => (
          <tr key={project.id} className={STATUS_ROW_CLASS[project.status] ?? ''}>
            <td><StarButton project={project} /></td>
            <td className="projectLink"><ProjectNameCell project={project} /></td>
            {!hideCompany && <td>{project.companyFirmName}</td>}
            <td>{formatLocaleDate(project.createdAt)}</td>
            <td>{project.numCollections}</td>
            <td><StatusCell project={project} /></td>
            {!hideRoles && <td><ProjectRoles project={project} /></td>}
          </tr>
        ))}
      </tbody>
    </IioTable>
  );
}
